import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'

import {
  CancelDowngradeSubscriptionPlan,
  CancelDowngradeSubscriptionPlanMutation,
  CancelDowngradeSubscriptionPlanMutationVariables,
  DowngradeSubscriptionPlan,
  DowngradeSubscriptionPlanMutation,
  DowngradeSubscriptionPlanMutationVariables,
  MembershipsQuery,
  MembershipsQueryVariables,
  PatronDetailFragmentDoc,
  PreviewSubscriptionUpdate,
  PreviewSubscriptionUpdateQuery,
  PreviewSubscriptionUpdateQueryVariables,
  ResumeSubscriptionPlanMutation,
  ResumeSubscriptionPlanMutationVariables,
  UpgradeSubscriptionPlan,
  UpgradeSubscriptionPlanMutation,
  UpgradeSubscriptionPlanMutationVariables,
} from '@/clients/utoniq-core/schema'
import { makeViewModel } from '@/core/architecture'
import { useSettingUserInfoModal } from '@/core/membership/useSettingUserInfoModal'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { showModal } = useSettingUserInfoModal()
  const { loading, data } = useQuery<
    MembershipsQuery,
    MembershipsQueryVariables
  >(MEMBERSHIPS)

  const { user } = data || {}
  const { organizationPatrons: memberships } = user || {}

  const [previewSubscriptionUpdate, { data: previewSubscriptionUpdateData }] =
    useLazyQuery<
      PreviewSubscriptionUpdateQuery,
      PreviewSubscriptionUpdateQueryVariables
    >(PreviewSubscriptionUpdate)

  const [stopSubscription, { loading: stoppingSubscription }] = useMutation(
    STOP_SUBSCRIPTION,
    {
      onCompleted({ stopSubscription }) {
        if (stopSubscription?.isPersonalInformationRequired) {
          showModal()
        }
      },
    }
  )

  const [upgradeSubscriptionPlan, { loading: upgradingSubscriptionPlan }] =
    useMutation<
      UpgradeSubscriptionPlanMutation,
      UpgradeSubscriptionPlanMutationVariables
    >(UpgradeSubscriptionPlan, {
      onCompleted({ upgradeSubscriptionPlan }) {
        if (upgradeSubscriptionPlan?.isPersonalInformationRequired) {
          showModal()
        }
      },
    })

  const [downgradeSubscriptionPlan, { loading: downgradingSubscriptionPlan }] =
    useMutation<
      DowngradeSubscriptionPlanMutation,
      DowngradeSubscriptionPlanMutationVariables
    >(DowngradeSubscriptionPlan, {
      onCompleted({ downgradeSubscriptionPlan }) {
        if (downgradeSubscriptionPlan?.isPersonalInformationRequired) {
          showModal()
        }
      },
    })

  const [
    cancelDowngradeSubscriptionPlan,
    { loading: cancellingChangeSubscription },
  ] = useMutation<
    CancelDowngradeSubscriptionPlanMutation,
    CancelDowngradeSubscriptionPlanMutationVariables
  >(CancelDowngradeSubscriptionPlan)

  const [resumeSubscriptionPlan, { loading: resumingSubscription }] =
    useMutation<
      ResumeSubscriptionPlanMutation,
      ResumeSubscriptionPlanMutationVariables
    >(RESUME_SUBSCRIPTION)

  return {
    state: {
      loading,
      stoppingSubscription,
      changingSubscription:
        upgradingSubscriptionPlan || downgradingSubscriptionPlan,
      cancellingChangeSubscription,
      resumingSubscription,
      memberships,
      isMembershipPersonalInformationRequired:
        !!user?.isMembershipPersonalInformationRequired,
      diffAmountSubscription:
        previewSubscriptionUpdateData?.previewSubscriptionUpdate.diffAmount ??
        0,
    },
    actions: {
      async onPreviewSubscriptionUpdate(artistId, planId) {
        await previewSubscriptionUpdate({
          variables: { artistId, planId },
        })
      },
      async onStopSubscription(artistId) {
        await stopSubscription({
          variables: { artistId },
          refetchQueries() {
            return [{ query: MEMBERSHIPS }]
          },
          awaitRefetchQueries: true,
        })
      },
      async onUpgradeSubscriptionPlan(planId, artistId) {
        await upgradeSubscriptionPlan({
          variables: { planId, artistId },
          refetchQueries() {
            return [{ query: MEMBERSHIPS }]
          },
          awaitRefetchQueries: true,
        })
      },
      async onDowngradeSubscriptionPlan(planId, artistId) {
        await downgradeSubscriptionPlan({
          variables: { planId, artistId },
          refetchQueries() {
            return [{ query: MEMBERSHIPS }]
          },
          awaitRefetchQueries: true,
        })
      },
      async onCancelChangeSubscription(artistId) {
        await cancelDowngradeSubscriptionPlan({
          variables: { artistId },
          refetchQueries() {
            return [{ query: MEMBERSHIPS }]
          },
          awaitRefetchQueries: true,
        })
      },
      async onResumeSubscription(artistId) {
        await resumeSubscriptionPlan({
          variables: { artistId },
          refetchQueries() {
            return [{ query: MEMBERSHIPS }]
          },
          awaitRefetchQueries: true,
        })
      },
    },
  }
})

const MEMBERSHIPS = gql`
  query memberships {
    user {
      id
      isMembershipPersonalInformationRequired
      organizationPatrons {
        ...PatronDetail
      }
    }
  }
  ${PatronDetailFragmentDoc}
`

const STOP_SUBSCRIPTION = gql`
  mutation stopSubscription($artistId: ID!) {
    stopSubscription(artistId: $artistId) {
      ...PatronDetail
    }
  }
  ${PatronDetailFragmentDoc}
`

const RESUME_SUBSCRIPTION = gql`
  mutation resumeSubscriptionPlan($artistId: String!) {
    resumeSubscriptionPlan(artistId: $artistId) {
      ...PatronDetail
    }
  }
  ${PatronDetailFragmentDoc}
`
